<template>
  <div class="model-params" :class="{ 'model-params--compact': compact }">
    <div
      v-for="param in visibleParams"
      :key="param.key"
      class="param-item"
    >
      <!-- 按钮组 -->
      <template v-if="param.type === 'button-group'">
        <div class="option-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <div :class="['btn-group', { 'btn-group--cols-3': param.equalCols === 3 }]">
            <button
              v-for="opt in param.options"
              :key="opt"
              :class="['btn-toggle', { active: isBtnActive(param, opt) }, { 'btn-toggle--cols-3': param.equalCols === 3 }]"
              :disabled="isOptionDisabled(param, opt)"
              @click="setValue(param.key, opt)"
            >{{ getOptionLabel(param, opt) }}</button>
          </div>
        </div>
      </template>

      <!-- 尺寸网格 -->
      <template v-else-if="param.type === 'size-grid'">
        <div class="option-group" style="width:100%;">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <div class="size-grid">
            <button
              v-for="opt in param.options"
              :key="typeof opt === 'object' ? opt.label : opt"
              :class="['size-btn', { active: isSizeActive(param, opt) }]"
              @click="selectSize(param, opt)"
            >
              <span class="size-label">{{ typeof opt === 'object' ? opt.label : opt }}</span>
            </button>
          </div>
        </div>
      </template>

      <!-- 卡片选择 -->
      <template v-else-if="param.type === 'card-select'">
        <div class="option-group" style="width:100%;">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <div class="gen-type-cards">
            <div
              v-for="opt in param.options"
              :key="opt"
              :class="['gen-type-card', { active: getValue(param.key) === opt }]"
              @click="setValue(param.key, opt)"
            >
              <span class="gen-type-card-name">{{ getOptionLabel(param, opt) }}</span>
              <span v-if="getOptionSubLabel(param, opt)" class="gen-type-card-desc">{{ getOptionSubLabel(param, opt) }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- 分辨率芯片 -->
      <template v-else-if="param.type === 'resolution-chips'">
        <div class="option-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <div class="resolution-chips">
            <button
              v-for="opt in param.options"
              :key="opt"
              :class="['resolution-chip', { active: getValue(param.key) === opt }]"
              @click="setValue(param.key, opt)"
            >{{ opt }}</button>
          </div>
        </div>
      </template>

      <!-- 滑块 -->
      <template v-else-if="param.type === 'slider'">
        <div class="option-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <div class="slider-row">
            <span class="slider-val">{{ getValue(param.key) }}{{ (param.props && param.props.unit) || '' }}</span>
            <input
              type="range"
              class="range-slider"
              :min="(param.props && param.props.min) || 0"
              :max="(param.props && param.props.max) || 100"
              :step="(param.props && param.props.step) || 1"
              :value="getValue(param.key)"
              @input="setValue(param.key, Number($event.target.value))"
            >
            <div class="slider-labels">
              <span>{{ (param.props && param.props.min) || 0 }}{{ (param.props && param.props.unit) || '' }}</span>
              <span>{{ (param.props && param.props.max) || 100 }}{{ (param.props && param.props.unit) || '' }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- 开关 -->
      <template v-else-if="param.type === 'switch'">
        <div class="switch-row">
          <span class="switch-title">{{ $t(param.label) }}</span>
          <label class="switch">
            <input type="checkbox" :checked="getValue(param.key)" @change="setValue(param.key, $event.target.checked)">
            <span class="switch-slider"></span>
          </label>
        </div>
      </template>

      <!-- 数字输入 -->
      <template v-else-if="param.type === 'number-input'">
        <div class="option-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <input
            type="number"
            class="seed-input"
            :value="getValue(param.key)"
            :min="param.props && param.props.min"
            :max="param.props && param.props.max"
            :step="param.props && param.props.step"
            :placeholder="param.props && param.props.placeholder"
            @input="setValue(param.key, Number($event.target.value))"
          >
        </div>
      </template>

      <!-- 自定义尺寸（宽 x 高） -->
      <template v-else-if="param.type === 'custom-size'">
        <div class="option-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <div class="custom-size-row">
            <input
              type="number"
              class="seed-input custom-size-input"
              :min="param.props && param.props.min"
              :max="param.props && param.props.max"
              :step="param.props && param.props.step"
              :value="getCustomSizeField(param.key, 'width')"
              :placeholder="$t('modelIntro.common.width')"
              @input="setCustomSizeField(param.key, 'width', $event.target.value)"
            >
            <input
              type="number"
              class="seed-input custom-size-input"
              :min="param.props && param.props.min"
              :max="param.props && param.props.max"
              :step="param.props && param.props.step"
              :value="getCustomSizeField(param.key, 'height')"
              :placeholder="$t('modelIntro.common.height')"
              @input="setCustomSizeField(param.key, 'height', $event.target.value)"
            >
          </div>
          <div v-if="param.props && param.props.hint" class="param-hint">{{ $t(param.props.hint) }}</div>
        </div>
      </template>

      <!-- 文本输入 -->
      <template v-else-if="param.type === 'text-input'">
        <div class="form-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
            <span v-if="param.optional" class="optional-label">({{ $t('modelIntro.common.optional') }})</span>
          </label>
          <textarea
            class="prompt-input prompt-input--small"
            :rows="(param.props && param.props.rows) || 2"
            :maxlength="param.props && param.props.maxlength"
            :value="getValue(param.key)"
            @input="setValue(param.key, $event.target.value)"
          ></textarea>
        </div>
      </template>

      <!-- 文件上传 -->
      <template v-else-if="param.type === 'upload'">
        <div class="form-group">
          <label v-if="param.label">
            {{ $t(param.label) }}
            <span v-if="param.required" class="required-dot">*</span>
          </label>
          <!-- 图片上传 -->
          <div
            v-if="!isAudioUpload(param) && !isVideoUpload(param)"
            class="upload-zone"
            :class="{ 'has-image': getValue(param.key), 'uploading': getUploadLoading(param.key) }"
            @click="triggerUpload(param)"
          >
            <template v-if="getUploadLoading(param.key)">
              <div class="loading-spinner"></div>
              <span>{{ $t('modelIntro.common.uploading') }}</span>
            </template>
            <template v-else-if="isEmptyUpload(param)">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <span>{{ $t('modelIntro.common.clickToUpload') }}</span>
              <span class="upload-hint">{{ getUploadAcceptHint(param) }}</span>
            </template>
            <template v-else>
              <!-- 多图上传（如 midjourney referenceImages） -->
              <template v-if="isArrayUpload(param)">
                <div class="upload-thumbnails" v-if="getValue(param.key) && getValue(param.key).length">
                  <div v-for="(img, idx) in getValue(param.key)" :key="idx" class="upload-thumb-item">
                    <img :src="img" class="upload-preview-sm" alt="">
                    <button class="remove-image-btn" @click.stop="removeArrayItem(param, idx)">×</button>
                  </div>
                </div>
              </template>
              <!-- 单图上传 -->
              <template v-else>
                <img :src="getValue(param.key)" class="upload-preview" alt="">
                <button class="remove-image-btn" @click.stop="removeUpload(param)">×</button>
              </template>
            </template>
          </div>
          <!-- 音频/视频上传 -->
          <div
            v-else
            class="upload-zone upload-zone-sm"
            :class="{
              'uploading': getUploadLoading(param.key),
              'upload-zone--has-video': isVideoUpload(param) && !isAudioUpload(param) && getValue(param.key)
            }"
            @click="triggerUpload(param)"
          >
            <template v-if="getUploadLoading(param.key)">
              <div class="loading-spinner"></div>
              <span>{{ $t('modelIntro.common.uploading') }}</span>
            </template>
            <template v-else-if="!getValue(param.key)">
              <span>{{ $t('modelIntro.common.clickToUpload') }}</span>
              <span class="upload-hint">{{ getUploadAcceptHint(param) }}</span>
            </template>
            <template v-else>
              <video
                v-if="isVideoUpload(param) && !isAudioUpload(param)"
                :src="proxiedUploadMediaUrl(getValue(param.key))"
                class="upload-preview upload-preview--video"
                controls
                muted
                playsinline
                preload="metadata"
                @click.stop
              ></video>
              <span v-else class="upload-preview-label">{{ getUploadFileName(param.key) }}</span>
              <button class="remove-image-btn" @click.stop="removeUpload(param)">×</button>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getModelParams } from '../../config/modelParams'
import { proxifyVolcArkTosFetchUrl } from '../../api/ai/official/volc-ark-tos-fetch-url'

export default {
  name: 'ModelParams',
  props: {
    /* 当前模型 ID */
    modelId: {
      type: String,
      required: true
    },
    /* compact = 首页简版，只显示 showInCompact 参数；full = 生成页完整版 */
    compact: {
      type: Boolean,
      default: false
    },
    /* 所有参数值，由父组件维护 */
    values: {
      type: Object,
      required: true
    },
    /* 上传中状态对象 { paramKey: boolean } */
    uploadLoading: {
      type: Object,
      default: function () { return {} }
    },
    /* 上传文件名对象 { paramKey: string } */
    uploadFileNames: {
      type: Object,
      default: function () { return {} }
    }
  },

  data: function () {
    return {
      /* 每个上传参数对应的隐藏 input 引用 */
      uploadInputs: {}
    }
  },

  computed: {
    /* 当前模型的参数配置 */
    paramConfig: function () {
      return getModelParams(this.modelId)
    },
    /* 根据条件过滤后的可见参数 */
    visibleParams: function () {
      if (!this.paramConfig) return []
      var self = this
      var params = this.paramConfig.params
      var result = []
      for (var i = 0; i < params.length; i++) {
        var p = params[i]
        /* compact 模式只显示 showInCompact 的参数 */
        if (self.compact && !p.showInCompact) continue
        /* hidden 类型不渲染 */
        if (p.type === 'hidden') continue
        /* 条件过滤 */
        if (p.condition && !p.condition(self.values)) continue
        /* compact 模式下使用 compactOptions / compactType 替代 */
        if (self.compact) {
          var overrides = {}
          if (p.compactOptions) overrides.options = p.compactOptions
          if (p.compactType) overrides.type = p.compactType
          if (Object.keys(overrides).length > 0) {
            p = Object.assign({}, p, overrides)
          }
        }
        result.push(p)
      }
      return result
    }
  },

  methods: {
    /* 获取参数值 */
    getValue: function (key) {
      return this.values[key]
    },

    /* 设置参数值 */
    setValue: function (key, value) {
      if (key === 'resolutionPreset') {
        var s = String(value || '')
        var m = s.match(/^(\d+)\*(\d+)$/)
        if (m) {
          this.$emit('change', 'customSize', { width: Number(m[1]), height: Number(m[2]) })
        }
      }
      this.$emit('change', key, value)
    },

    /* 判断按钮组选项是否选中（兼容对象类型选项） */
    isBtnActive: function (param, opt) {
      var current = this.getValue(param.key)
      if (typeof opt === 'object' && opt !== null) {
        return current && current.label === opt.label
      }
      return current === opt
    },

    /* 判断按钮组选项是否禁用 */
    isOptionDisabled: function (param, opt) {
      if (param.props && param.props.disableOption) {
        return param.props.disableOption(opt, this.values)
      }
      return false
    },

    /* 判断尺寸网格选项是否选中 */
    isSizeActive: function (param, opt) {
      var current = this.getValue(param.key)
      if (typeof opt === 'object') {
        return current && current.label === opt.label
      }
      return current === opt
    },

    /* 选择尺寸 */
    selectSize: function (param, opt) {
      this.setValue(param.key, typeof opt === 'object' ? opt : opt)
    },

    /* 获取选项显示文字 */
    getOptionLabel: function (param, opt) {
      /* 对象类型选项（如 size-grid），取 label 字段 */
      if (typeof opt === 'object' && opt !== null) {
        return opt.label || String(opt)
      }
      if (param.optionLabels && param.optionLabels[opt]) {
        var label = param.optionLabels[opt]
        /* 尝试翻译，如果 i18n key 不存在则直接返回 */
        if (label.indexOf('.') > -1) {
          try { return this.$t(label) } catch (e) { return label }
        }
        return label
      }
      return String(opt)
    },

    getOptionSubLabel: function (param, opt) {
      if (!param.optionSubLabels || !param.optionSubLabels[opt]) return ''
      var label = param.optionSubLabels[opt]
      if (label.indexOf('.') > -1) {
        try { return this.$t(label) } catch (e) { return label }
      }
      return label
    },

    /* 判断是否是音频上传 */
    isAudioUpload: function (param) {
      return param.props && param.props.accept && param.props.accept.indexOf('.mp3') > -1
    },

    /* 判断是否是视频上传（含 video/*；与 isAudioUpload 互斥时用 !isAudioUpload） */
    isVideoUpload: function (param) {
      if (!param.props || !param.props.accept) return false
      var a = param.props.accept
      return a.indexOf('video/') > -1
    },

    /** 上传结果 URL：本地 dev 下 TOS 等走同源代理，便于 video 拉流 */
    proxiedUploadMediaUrl: function (url) {
      var s = String(url || '').trim()
      if (!s) return ''
      if (s.indexOf('blob:') === 0 || s.indexOf('data:') === 0) return s
      return proxifyVolcArkTosFetchUrl(s)
    },

    /* 判断是否是多图上传（数组类型：midjourney base64 / pollodance URL 多图） */
    isArrayUpload: function (param) {
      return param.props && (param.props.base64 === true || param.props.multiUrl === true)
    },

    /* 判断上传值是否为空（兼容数组类型，空数组也视为空） */
    isEmptyUpload: function (param) {
      var val = this.getValue(param.key)
      if (!val) return true
      if (Array.isArray(val) && val.length === 0) return true
      return false
    },

    /* 获取上传限制提示文字 */
    getUploadAcceptHint: function (param) {
      if (!param.props || !param.props.accept) return ''
      var accept = param.props.accept
      if (accept.indexOf('image/') > -1) return 'PNG, JPG, JPEG' + (accept.indexOf('webp') > -1 ? ', WebP' : '')
      if (accept.indexOf('.mp3') > -1) return 'MP3, WAV, M4A, MP4'
      if (accept.indexOf('video/') > -1) return 'MP4'
      return ''
    },

    /* 获取上传 loading 状态 */
    getUploadLoading: function (key) {
      return !!this.uploadLoading[key]
    },

    /* 获取上传文件名 */
    getUploadFileName: function (key) {
      return this.uploadFileNames[key] || ''
    },

    getCustomSizeField: function (key, field) {
      var val = this.getValue(key)
      if (!val || typeof val !== 'object') return ''
      return val[field] != null ? val[field] : ''
    },

    setCustomSizeField: function (key, field, rawValue) {
      var val = this.getValue(key)
      var next = (val && typeof val === 'object') ? Object.assign({}, val) : { width: '', height: '' }
      var n = parseInt(rawValue, 10)
      next[field] = isNaN(n) ? '' : n
      this.setValue(key, next)
      this.$emit('change', 'resolutionPreset', 'custom')
    },

    /* 触发文件上传 */
    triggerUpload: function (param) {
      var self = this
      /* 创建隐藏的 file input */
      var input = document.createElement('input')
      input.type = 'file'
      if (param.props && param.props.accept) {
        input.accept = param.props.accept
      }
      input.onchange = function (e) {
        var file = e.target.files && e.target.files[0]
        if (!file) return
        self.$emit('upload', { paramKey: param.key, file: file, param: param })
      }
      input.click()
    },

    /* 移除已上传的文件 */
    removeUpload: function (param) {
      this.setValue(param.key, param.default || null)
      this.$emit('remove-upload', param.key)
    },

    /* 移除数组类型上传中的某一项（如 midjourney 多图） */
    removeArrayItem: function (param, index) {
      var arr = this.getValue(param.key)
      if (arr && Array.isArray(arr)) {
        var newArr = arr.slice()
        newArr.splice(index, 1)
        this.setValue(param.key, newArr)
      }
    }
  }
}
</script>

<style scoped>
.model-params {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* 复用公共 CSS 中的类名，此处只补充组件内部需要的样式 */

/* 尺寸网格（从 FluxPro11UltraDetail.vue 提取） */
.size-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 8px;
}
.size-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
}
.size-btn:hover {
  border-color: var(--accent, #059669);
}
.size-btn.active {
  border-color: var(--accent, #059669);
  background: rgba(5, 150, 105, 0.1);
}
.size-label {
  font-size: 12px;
  color: var(--text-secondary, #666);
}
.size-btn.active .size-label {
  color: var(--accent, #059669);
  font-weight: 600;
}

/* 卡片选择 */
.gen-type-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 8px;
}
.gen-type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: all 0.2s;
}
.gen-type-card:hover {
  border-color: var(--accent, #059669);
}
.gen-type-card.active {
  border-color: var(--accent, #059669);
  background: rgba(5, 150, 105, 0.1);
}
.gen-type-card-name {
  font-size: 14px;
  font-weight: 600;
}

/* 分辨率芯片 */
.resolution-chips {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.resolution-chip {
  padding: 8px 20px;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
}
.resolution-chip:hover {
  border-color: var(--accent, #059669);
}
.resolution-chip.active {
  border-color: var(--accent, #059669);
  background: rgba(5, 150, 105, 0.1);
  color: var(--accent, #059669);
}

/* 小号文本输入 */
.prompt-input--small {
  font-size: 13px !important;
}

.required-dot {
  color: #ef4444;
  margin-left: 4px;
  font-weight: 700;
}

.custom-size-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 8px;
}

.custom-size-input {
  width: 100%;
}

.param-hint {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-muted, #6b7280);
}

.btn-group--cols-3 {
  width: 100%;
}

.btn-toggle--cols-3 {
  flex: 0 0 calc((100% - 20px) / 3);
  max-width: calc((100% - 20px) / 3);
  min-width: 0;
}

/* 上传缩略图列表 */
.upload-thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.upload-thumb-item {
  position: relative;
}
.upload-thumb-item .remove-image-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0,0,0,0.6);
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  padding: 0;
}

/* compact 模式下调整间距 */
.model-params--compact .option-group {
  margin-bottom: 8px;
}
.model-params--compact .form-group {
  margin-bottom: 8px;
}
.model-params--compact {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.model-params--compact .param-item {
  margin: 0;
}

/* 参考视频上传：预览块纵向排布，避免与删除按钮挤在一行 */
.upload-zone--has-video {
  flex-direction: column;
  align-items: stretch;
}
.upload-preview--video {
  width: 100%;
  max-height: 220px;
  background: #000;
}

/* 响应式 */
@media (max-width: 768px) {
  .size-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .gen-type-cards {
    grid-template-columns: 1fr;
  }
}
</style>
